import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import apiService from '../services/apiService';
import { useFasesDosVendedores, FaseBadge } from '../components/FaseBadge';
import Loading from '../components/Loading';
import AwonJudgePanel, { intervalo } from './AwonJudgePanel';
import './AwonMonitor.css';

/**
 * AWON Monitor — painel admin do pipeline always-on.
 *
 * Seções:
 *  - Cards de visão geral (sessões, chunks, fila SQS)
 *  - Sessões por vendedor (período)
 *  - Atendimentos recentes (player de áudio com URL pré-assinada de 5 min,
 *    transcrição, eventos e ações de revisão aprovar/descartar)
 *  - Início e fim dos atendimentos: aviso de que o Jev decide (frases-chave não são usadas no recorte)
 *    (fusão aug_or — a frase-chave entra como pista no prompt e faz OR com a
 *    decisão do modelo), então a seção fica SEMPRE visível.
 */

// textos de ajuda ao passar o mouse nos cards e colunas (o title nativo respeita as quebras de linha)
const tip = (titulo, ...itens) => [titulo, '', ...itens.map((t) => `• ${t}`)].join('\n');
const TIP = {
    atendimentos: tip('Recortes de atendimento feitos pelo worker1 no período.',
        'Fechados: recorte concluído.',
        'Abertos: atendimento ainda em andamento.'),
    colRevisao: tip('O que espera uma pessoa decidir no período.',
        'Com o julgador em execução: os indecisos (os outros recortes esperam o julgador).',
        'Sem o julgador: os recortes em revisão.'),
    chunks: tip('Trechos de 20 s com som recebidos no período (silenciosos ficam de fora).',
        'Aguardando: ainda não consumidos pelo worker1 (inclui vendedor sem voz cadastrada, que é ignorado).',
        'Descartados: processados, mas fora de qualquer recorte (trecho sem cliente).',
        'Válidos: fazem parte de algum atendimento recortado.',
        'Silenciosos: sem fala, descartados na entrada.',
        'Nenhum chunk é apagado: todos continuam no S3.'),
    fila: tip('Transcrição do áudio deste cliente.',
        'Motor: ElevenLabs ou transcriber da Aliado (Whisper).',
        'Número grande: blocos esperando para ser transcritos.',
        'Em processamento: transcrição em andamento.',
        'DLQ: mensagens que falharam 5 vezes.',
        'Sem a fila única, cada worker transcreve o próprio áudio.'),
    ultimo: tip('Último trecho de áudio recebido deste cliente e de cada vendedor ativo no período.',
        'Horário de Brasília; a data só aparece quando não é de hoje.'),
    rodadas: tip('Janelas que o worker2 avaliou: em geral de 5 min; no começo e no fim da gravação podem ser menores.',
        'Cada janela lê os últimos 20 min de gravação.',
        'Com cliente: havia cliente na conversa; só essas passam pela IA.'),
    erros: tip('Janelas em que a transcrição ou a IA falhou.',
        'A janela fica registrada com o erro.'),
    atraso: tip('Atraso entre o fim da janela de 5 min e o fim da avaliação do worker2.',
        'Número grande: mediana.',
        'Máx.: maior atraso do período.',
        'Último: quando a última janela foi processada.',
        'Só conta janelas processadas no mesmo dia.'),
    blocos: tip('Blocos de 1 min transcritos uma única vez e lidos pelos dois workers.',
        'Sem fala: pulados, não vão para o motor.',
        'Com erro: falha do motor de transcrição.'),
    descartados: tip('Processados pelo worker1 sem entrar em nenhum recorte.',
        'Motivo: trecho sem cliente (vendedor sozinho, conversa entre vendedores, intervalo, início ou fim da gravação).',
        'No modo awon-behaviour o worker2 avalia esses trechos mesmo assim.',
        'O áudio continua no S3.'),
    filaW1: tip('Mensagens esperando o worker1 agora.',
        'Em processamento: recebidas e ainda não concluídas.',
        'DLQ: mensagens que falharam 5 vezes.'),
    filaW2: tip('Mensagens esperando o worker2 agora.',
        'Em processamento: recebidas e ainda não concluídas.',
        'DLQ: mensagens que falharam 5 vezes.'),
    horas: tip('Horas gravadas pelo vendedor no período.',
        'Total bruto, mesma conta da revisão de pontos: todo chunk conta pela duração real, inclusive silêncio e microfone mudo.',
        'O dia é o da gravação, no horário de Brasília.',
        'Passe o mouse no número para ver por dia.'),
    horasSom: tip('Horas gravadas pelo vendedor no período, descontando o silêncio.',
        'Só chunks com som (acima de -45 dB), pela duração real — os de silêncio ou microfone mudo ficam de fora.',
        'Passe o mouse no número para ver por dia.'),
    colHorasTotal: tip('Horas gravadas no período, somando os vendedores ativos do cliente.',
        'Total bruto: todo chunk recebido conta pela duração real, inclusive silêncio e microfone mudo.',
        'É a mesma régua da coluna de horas dos vendedores e da Revisão de pontos.'),
};

// Junta os 50 recortes recentes com os pendentes do juiz de IA (podem ser mais antigos), sem duplicar.
// todas as páginas do período (a API devolve no máximo 100 por vez): um dia pode ter mais de 100 recortes, e as listas
// e os cards têm de contar o período inteiro
const buscarTodas = async (sub, params) => {
    const todas = [];
    for (let offset = 0; ; offset += 100) {
        const lote = (await apiService.awonGetSessions(sub, { ...params, limit: 100, offset })).sessions || [];
        todas.push(...lote);
        if (lote.length < 100) return { sessions: todas };
    }
};

const juntarSessoes = (...listas) => {
    const m = new Map();
    listas.flat().filter(Boolean).forEach((x) => m.set(x.id, x));
    return [...m.values()].sort((x, y) => y.id - x.id);
};

const AI_LABEL = { aprovado_ia: 'aprovado pela IA', reprovado_ia: 'reprovado pela IA', indeciso: 'IA indecisa',
    dividido: 'dividido pela IA' };
const STATUS_LABEL = { split: 'dividido' };
// popup da nova avaliação: como cada decisão aparece (classe = cor do cartão)
const REAV_LABEL = {
    aprovado_ia: { txt: 'Aprovado pela IA', cls: 'ok', icon: '✓' },
    reprovado_ia: { txt: 'Reprovado pela IA', cls: 'no', icon: '✕' },
    indeciso: { txt: 'Continua em revisão', cls: 'rev', icon: '⏸' },
    dividido: { txt: 'Dividido', cls: 'split', icon: '✂' },
};

// Player que só baixa o áudio quando a pessoa clica: o arquivo vem inteiro pelo servidor do admin (sem link do S3
// no navegador, sem cache) e fica só na memória da aba — é liberado quando o player fecha ou a aba é fechada.
const AudioSobDemanda = ({ subdomain, params, className }) => {
    const [src, setSrc] = useState(null);
    const [estado, setEstado] = useState('');
    useEffect(() => () => { if (src) URL.revokeObjectURL(src); }, [src]);
    const carregar = async () => {
        setEstado('carregando');
        try {
            const blob = await apiService.awonGetAudio(subdomain, params);
            setSrc(URL.createObjectURL(blob));
            setEstado('');
        } catch (e) {
            setEstado('erro');
        }
    };
    if (src) {
        return (
            <audio controls autoPlay controlsList="nodownload" src={src} className={className}
                   onContextMenu={(e) => e.preventDefault()}>
                Seu navegador não suporta áudio.
            </audio>
        );
    }
    return (
        <span>
            <button className="awon-btn awon-btn-ghost" onClick={carregar} disabled={estado === 'carregando'}>
                {estado === 'carregando' ? 'Carregando áudio…' : '▶ Ouvir áudio'}
            </button>
            {estado === 'erro' && <span className="awon-muted"> não foi possível carregar — tente de novo</span>}
        </span>
    );
};

const AwonMonitor = () => {
    const [tenants, setTenants] = useState([]);     // [{subdomain, name}]
    const [overview, setOverview] = useState({});   // subdomain -> stats
    // Vazio até os tenants chegarem: começar em 'demo' fazia a tela buscar os dados dele
    // antes de saber se este admin pode vê-lo (allowlist de core.admin_companies).
    const [subdomain, setSubdomain] = useState('');
    // Fase de todos os vendedores da empresa numa chamada (§9.5).
    const { fases: fasesVend } = useFasesDosVendedores({ subdomain });
    const [days, setDays] = useState(7);
    // período por DATAS (AAAA-MM-DD): preenchidas, o atalho de dias deixa de valer. A API usa sempre o dia inteiro
    // no horário de Brasília (início 00:00:00, fim 23:59:59). Vale para tudo que tem data na página, menos as vozes.
    const [dataIni, setDataIni] = useState('');
    const [dataFim, setDataFim] = useState('');
    const periodo = useMemo(() => ((dataIni || dataFim)
        ? { date_start: dataIni || dataFim, date_end: dataFim || dataIni } : { days }), [days, dataIni, dataFim]);
    const dataBr = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
    const periodoTitulo = periodo.days
        ? (periodo.days === 1 ? 'Hoje' : `últimos ${periodo.days} dias`)
        : (periodo.date_start === periodo.date_end ? dataBr(periodo.date_start)
            : `${dataBr(periodo.date_start)} a ${dataBr(periodo.date_end)}`);
    const periodoCurto = periodo.days ? (periodo.days === 1 ? 'hoje' : `${periodo.days}d`) : periodoTitulo;

    const [stats, setStats] = useState(null);
    const [queue, setQueue] = useState(null);
    const [behavior, setBehavior] = useState(null);   // worker2 (modo awon-behaviour)
    const [sessions, setSessions] = useState([]);
    const [phrases, setPhrases] = useState([]);
    const [phraseQuality, setPhraseQuality] = useState({});
    const [catalog, setCatalog] = useState({ stores: [], salesmen: [] });
    const [voices, setVoices] = useState({ enrolled: [], missing: [] });

    // formulário de nova frase + edição inline
    const emptyForm = { kind: 'start', scope: 'store', store_id: '', salesman_id: '', phrase: '' };
    const [newPhrase, setNewPhrase] = useState(emptyForm);
    const [savingPhrase, setSavingPhrase] = useState(false);
    const [editingPhrase, setEditingPhrase] = useState(null); // {id, phrase}

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [expandedSession, setExpandedSession] = useState(null);
    const [openDays, setOpenDays] = useState(() => new Set()); // datas expandidas (por lista)
    const [phrasesOpen, setPhrasesOpen] = useState(false);       // seção de frases-chave recolhida por padrão
    const [actioning, setActioning] = useState({});
    const [confirmDiscard, setConfirmDiscard] = useState(null); // sessão a confirmar descarte
    // juiz de IA dos recortes em revisão: automático por cliente (padrão desligado) + histórico de quem mudou
    const [behOpen, setBehOpen] = useState(null);           // comportamento expandido (vendedores que realizaram)
    // seções recolhíveis (Áudio recebido, Julgador smart, Worker1 – recortes, Worker2 – comportamentos, Vozes): todas
    // FECHADAS por padrão e só UMA aberta
    // por vez. O link do Slack (?session=) abre Recortes, onde ficam as listas de revisão e da IA.
    const [secaoAberta, setSecaoAberta] = useState(() => {
        try {
            const q = new URLSearchParams(window.location.search);
            return q.get('indeciso') ? 'julgador' : q.get('session') ? 'recortes' : null;
        } catch (e) { return null; }
    });
    const fechadas = { has: (k) => secaoAberta !== k };
    const toggleSecao = (k) => setSecaoAberta((prev) => (prev === k ? null : k));
    const cabecalhoSecao = (k) => ({
        role: 'button', tabIndex: 0, 'aria-expanded': !fechadas.has(k),
        title: fechadas.has(k) ? 'Clique para abrir a seção' : 'Clique para recolher a seção',
        onClick: () => toggleSecao(k),
        onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSecao(k); } },
    });
    const caretSecao = (k) => <span className="awon-group-caret">{fechadas.has(k) ? '▸' : '▾'}</span>;
    // "Nova avaliação da IA" de um recorte: popup com o antes e o agora ({ session, status, res, erro })
    const [reavaliacao, setReavaliacao] = useState(null);
    // "Corrigir análise": { session, fase: editar|analisando|proposta|aplicando|pronto|erro, texto, pendente (id da
    // proposta recusada, registrada ao reenviar), proposta, final, erro, verPrompt }
    const [correcao, setCorrecao] = useState(null);
    // lista única do Worker1: recortes que o julgador smart ainda não processou ({ ids: Set, mode, next_round })
    const [aguardando, setAguardando] = useState(null);
    // modo do julgador na empresa (interruptor): em execução as listas de antes somem — a decisão é do julgador
    const [modoJulgador, setModoJulgador] = useState(null);
    // julgador ativo (execução, ou 'manual' em QA — rodadas só pelos botões): o worker2 só indica comportamentos ao
    // julgador (sem atendimento nem pontos; o n8n pontua)
    const julgadorAtivo = ['execucao', 'manual'].includes(modoJulgador);
    const w2SoIndica = julgadorAtivo;
    const buscarAguardando = (sub, per) => Promise.all([
        apiService.awonJudgePending(sub, intervalo(per)),
        apiService.awonJudgeConfig(sub).catch(() => ({ mode: null })),
    ]).then(([r, cfg]) => {
        setModoJulgador(cfg.mode);
        return { ids: new Set(r.pending_ids || []), mode: ['execucao', 'manual'].includes(cfg.mode) ? cfg.mode : r.mode,
            next_round: cfg.mode === 'manual' ? null : r.next_round };
    }).catch(() => null);
    // link do Slack: /awon-monitor?subdomain=X&session=ID abre direto o recorte
    const deepLink = useRef((() => {
        try {
            const q = new URLSearchParams(window.location.search);
            const id = Number(q.get('session'));
            const ind = Number(q.get('indeciso'));    // link do MCP: abre o indeciso no Julgador smart
            return { subdomain: q.get('subdomain') || '', session: Number.isFinite(id) && id > 0 ? id : null,
                     indeciso: Number.isFinite(ind) && ind > 0 ? ind : null };
        } catch (e) { return { subdomain: '', session: null }; }
    })());
    const [lastUpdated, setLastUpdated] = useState(null);
    // atualização automática a cada 3 s (só os dados que mudam; lembrada no navegador)
    const [autoRefresh, setAutoRefresh] = useState(() => {
        try { return localStorage.getItem('awonAutoRefresh') !== '0'; } catch (e) { return true; }
    });
    const liveBusy = useRef(false);
    const sessionsRef = useRef(null);
    // recortes abertos pelo id fora dos 50 recentes (link da Revisão de pontos, completo de um dividido): seguem na
    // lista nas atualizações seguintes
    const extras = useRef({ sub: '', ids: new Set() });
    const buscarExtras = (sub) => (extras.current.sub === sub && extras.current.ids.size
        ? apiService.awonGetSessions(sub, { ids: [...extras.current.ids].join(','), limit: 100 })
            .then((r) => r.sessions || []).catch(() => [])
        : Promise.resolve([]));

    const loadSessions = useCallback(async (sub, filters, per) => {
        // sem filtro de revisão: trazemos os dois (revisão + recortados) e
        // separamos em duas listas na renderização; as duas no período (pelo início do atendimento)
        const [data, pend, extra] = await Promise.all([
            buscarTodas(sub, { ...filters, ...per }),
            // decididos pela IA e ainda sem ação humana (com o filtro de status, ficariam de fora)
            buscarTodas(sub, { ai_pending: true, ...per }).catch(() => ({ sessions: [] })),
            buscarExtras(sub),
        ]);
        setSessions(juntarSessoes(data.sessions, pend.sessions, extra));
        setAguardando(await buscarAguardando(sub, per));
    }, []);

    const loadAll = useCallback(async () => {
        // ainda resolvendo qual empresa; o efeito dos tenants dispara isto de novo
        if (!subdomain) return;
        try {
            setLoading(true);
            setError('');
            const filters = {};
            if (statusFilter) filters.session_status = statusFilter;

            const [statsData, queueData] = await Promise.all([
                apiService.awonGetStats(subdomain, periodo),
                apiService.awonGetQueueStatus(),
            ]);
            setStats(statsData);
            setQueue(queueData);
            // worker2 (comportamentos): tenant sem as tabelas devolve enabled=false
            try {
                setBehavior(await apiService.awonGetBehavior(subdomain, periodo));
            } catch (e) {
                console.error('Erro ao carregar comportamentos:', e);
                setBehavior(null);
            }
            await loadSessions(subdomain, filters, periodo);
            const phrasesData = await apiService.awonGetKeyPhrases(subdomain);
            setPhrases(phrasesData.phrases || []);
            // catálogo p/ os seletores do cadastro de frases
            try {
                const cat = await apiService.awonGetCatalog(subdomain);
                setCatalog({ stores: cat.stores || [], salesmen: cat.salesmen || [] });
            } catch (e) {
                console.error('Erro ao carregar catálogo:', e);
            }
            // vozes cadastradas (embeddings) + vendedores sem voz
            try {
                const emb = await apiService.awonGetEmbeddings(subdomain);
                setVoices({ enrolled: emb.enrolled || [], missing: emb.missing || [] });
            } catch (e) {
                console.error('Erro ao carregar vozes:', e);
            }
            // qualidade por frase (feedback da revisão humana, últimos 90 dias)
            try {
                const pq = await apiService.awonGetPhraseQuality(subdomain, 90);
                const map = {};
                (pq.phrases || []).forEach((q) => { map[`${q.kind}|${q.phrase}`] = q; });
                setPhraseQuality(map);
            } catch (e) {
                console.error('Erro ao carregar qualidade das frases:', e);
            }
            setLastUpdated(new Date());
        } catch (err) {
            console.error('❌ Erro ao carregar monitor AWON:', err);
            setError(err.message || 'Erro ao carregar dados');
        } finally {
            setLoading(false);
        }
    }, [subdomain, periodo, statusFilter, loadSessions]);

    useEffect(() => {
        // tenants só na montagem
        apiService.awonGetTenants()
            .then((data) => {
                const list = data.tenants || [];
                setTenants(list);
                // 'demo' segue sendo o padrão de quem pode vê-lo; admin restrito à allowlist
                // cai na primeira empresa permitida.
                const doLink = deepLink.current.subdomain;
                const inicial = doLink && list.some((t) => t.subdomain === doLink) ? doLink
                    : list.some((t) => t.subdomain === 'demo') ? 'demo' : list[0]?.subdomain;
                if (inicial) {
                    setSubdomain(inicial);
                } else {
                    // nenhuma empresa disponível: sem isto a tela ficaria no spinner para sempre
                    setError('Nenhuma empresa com AWON disponível para o seu acesso');
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.error('Erro ao listar tenants AWON:', err);
                setError('Não foi possível listar as empresas do AWON');
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    // Ciclo leve: cards, filas, comportamentos e recortes, sem indicador de carregando. O link de áudio de um
    // recorte já na tela só é trocado perto de expirar (vale 5 min), para não interromper quem está ouvindo.
    const refreshLive = useCallback(async () => {
        if (!subdomain || liveBusy.current || document.hidden || confirmDiscard) return;
        liveBusy.current = true;
        try {
            const filters = statusFilter ? { session_status: statusFilter } : {};
            const [statsData, queueData, behaviorData, sessData, pendData, extraData, aguardData] = await Promise.all([
                apiService.awonGetStats(subdomain, periodo),
                apiService.awonGetQueueStatus(),
                apiService.awonGetBehavior(subdomain, periodo).catch(() => null),
                buscarTodas(subdomain, { ...filters, ...periodo }),
                buscarTodas(subdomain, { ai_pending: true, ...periodo }).catch(() => ({ sessions: [] })),
                buscarExtras(subdomain),
                buscarAguardando(subdomain, periodo),
            ]);
            setStats(statsData);
            setQueue(queueData);
            if (behaviorData) setBehavior(behaviorData);
            setSessions(juntarSessoes(sessData.sessions, pendData.sessions, extraData));
            if (aguardData) setAguardando(aguardData);
            setLastUpdated(new Date());
        } catch (e) {
            // silencioso: o próximo ciclo tenta de novo (o botão Atualizar mostra o erro)
        } finally {
            liveBusy.current = false;
        }
    }, [subdomain, periodo, statusFilter, confirmDiscard]);

    useEffect(() => {
        try { localStorage.setItem('awonAutoRefresh', autoRefresh ? '1' : '0'); } catch (e) { /* sem storage */ }
        if (!autoRefresh) return undefined;
        const id = setInterval(refreshLive, 3000);
        return () => clearInterval(id);
    }, [autoRefresh, refreshLive]);

    // partes de recortes divididos que não vieram na lista (outro período ou fora dos recentes): busca pelo id para a
    // caixa do completo mostrar para onde foi cada parte; seguem carregadas nas atualizações (extras)
    useEffect(() => {
        if (!subdomain) return;
        const faltam = sessions.filter((x) => x.status === 'split')
            .flatMap((x) => (Array.isArray(x.diarization?.split_children) ? x.diarization.split_children : []))
            .filter((id) => !sessions.some((y) => y.id === id));
        if (!faltam.length) return;
        if (extras.current.sub !== subdomain) extras.current = { sub: subdomain, ids: new Set() };
        const novos = faltam.filter((id) => !extras.current.ids.has(id));
        if (!novos.length) return;
        novos.forEach((id) => extras.current.ids.add(id));
        apiService.awonGetSessions(subdomain, { ids: novos.slice(0, 100).join(','), limit: 100 })
            .then((r) => setSessions((prev) => juntarSessoes(prev, r.sessions || [])))
            .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessions, subdomain]);

    // visão geral POR CLIENTE (um stats por tenant, em paralelo)
    useEffect(() => {
        if (!tenants.length) return;
        Promise.all(tenants.map((t) =>
            apiService.awonGetStats(t.subdomain, periodo)
                .then((s) => [t.subdomain, s])
                .catch(() => [t.subdomain, null])
        )).then((pairs) => setOverview(Object.fromEntries(pairs)));
    }, [tenants, periodo]);

    // link do Slack: abre o dia e o recorte e rola até ele (uma vez)
    useEffect(() => {
        const id = deepLink.current.session;
        if (!id || !sessions.length) return;
        deepLink.current.session = null;
        irParaRecorte(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessions]);

    // abre o grupo do dia do recorte, expande e rola até ele (link do Slack / da Revisão de pontos; pai <-> filhos de
    // um recorte dividido). Fora dos carregados (50 recentes + pendentes da IA): busca pelo id
    const irParaRecorte = async (id) => {
        let s = sessions.find((x) => x.id === id);
        if (!s) {
            try {
                s = ((await apiService.awonGetSessions(subdomain, { ids: String(id), limit: 1 })).sessions || [])[0];
            } catch (e) { s = null; }
            if (!s) {
                setError(`Recorte #${id} não encontrado.`);
                return;
            }
            if (extras.current.sub !== subdomain) extras.current = { sub: subdomain, ids: new Set() };
            extras.current.ids.add(id);
            setSessions((prev) => juntarSessoes(prev, [s]));
        }
        const prefix = isAiOk(s) ? 'aiok' : isAiNo(s) ? 'aino'
            : (s.needs_review && s.status !== 'discarded') ? 'review' : s.status === 'split' ? 'split' : 'cut';
        setSecaoAberta('recortes');
        setOpenDays((prev) => new Set(prev).add(`${prefix}:${dayKey(s.window_start)}`));
        setExpandedSession(id);
        setTimeout(() => document.getElementById(`awon-session-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    };

    // --- juiz de IA ---------------------------------------------------------
    const aiAction = async (session, action) => {
        try {
            setActioning((prev) => ({ ...prev, [session.id]: action }));
            await apiService.awonAiAction(subdomain, session.id, action);
            await loadAll();
        } catch (err) {
            setError(err.message || 'Erro na ação do juiz de IA');
        } finally {
            setActioning((prev) => { const n = { ...prev }; delete n[session.id]; return n; });
        }
    };
    const novaAvaliacao = async (session) => {
        setReavaliacao({ session, status: 'rodando' });
        try {
            const res = await apiService.awonAiRun(subdomain, session.id);
            const falha = res.error || res.skipped;
            setReavaliacao({ session, status: falha ? 'erro' : 'pronto', res, erro: falha });
            refreshLive();
        } catch (err) {
            setReavaliacao({ session, status: 'erro', erro: err.message });
        }
    };
    const abrirCorrecao = (session) => setCorrecao({ session, fase: 'editar', texto: '' });
    const correcaoAnalisar = async () => {
        const c = correcao;
        setCorrecao({ ...c, fase: 'analisando', erro: null });
        try {
            // a proposta recusada é registrada agora, com o comentário novo (a próxima proposta vê a anterior)
            if (c.pendente) await apiService.awonCorrectionReject(subdomain, c.pendente, c.texto);
            const r = await apiService.awonCorrectionPropose(subdomain, c.session.id, c.texto, c.pendente || null);
            setCorrecao({ ...c, fase: 'proposta', proposta: r, pendente: null, verPrompt: false });
        } catch (err) {
            setCorrecao({ ...c, fase: 'erro', erro: err.message });
        }
    };
    const correcaoNaoAceitar = () => setCorrecao((c) => ({ ...c, fase: 'editar', pendente: c.proposta.correction_id }));
    const correcaoAceitar = async () => {
        const c = correcao;
        setCorrecao({ ...c, fase: 'aplicando' });
        try {
            const r = await apiService.awonCorrectionAccept(subdomain, c.proposta.correction_id);
            setCorrecao({ ...c, fase: 'pronto', final: r });
            refreshLive();
        } catch (err) {
            setCorrecao({ ...c, fase: 'erro', erro: err.message });
        }
    };
    const fecharCorrecao = async () => {
        const c = correcao;
        setCorrecao(null);
        // fechou com proposta na tela ou recusada sem reenviar: fica registrada como não aceita
        const id = c?.fase === 'proposta' ? c.proposta?.correction_id : c?.pendente;
        if (id) { try { await apiService.awonCorrectionReject(subdomain, id, null); } catch (e) { /* já decidida */ } }
    };

    // --- ações de revisão -------------------------------------------------
    const reviewSession = async (session, action) => {
        // subdomain explícito: salesman_id é ambíguo entre tenants (a API resolveria
        // o tenant errado e dava 404 ao aprovar/descartar)
        const payload = { id: session.id, salesman_id: session.salesman_id, subdomain };
        if (action === 'approve') {
            payload.needs_review = false;
            payload.review_reason = null;
        } else if (action === 'discard') {
            payload.status = 'discarded';
            payload.needs_review = false;
        }
        try {
            setActioning((prev) => ({ ...prev, [session.id]: true }));
            await apiService.awonUpdateSession(payload);
            // tira o item da lista e do card de revisão imediatamente; loadAll sincroniza
            setSessions((prev) => prev.filter((x) => x.id !== session.id));
            setStats((prev) => (prev && session.needs_review
                ? { ...prev, sessions: { ...prev.sessions, needs_review: Math.max(0, (prev.sessions.needs_review || 0) - 1) } }
                : prev));
            await loadAll();
        } catch (err) {
            setError(err.message || 'Erro ao atualizar atendimento');
        } finally {
            setActioning((prev) => ({ ...prev, [session.id]: false }));
        }
    };

    const reloadPhrases = async () => {
        const phrasesData = await apiService.awonGetKeyPhrases(subdomain);
        setPhrases(phrasesData.phrases || []);
    };

    const savePhrase = async () => {
        const f = newPhrase;
        if (!f.phrase.trim()) { setError('Digite a frase.'); return; }
        if (f.scope === 'store' && !f.store_id) { setError('Selecione a loja.'); return; }
        if (f.scope === 'salesman' && !f.salesman_id) { setError('Selecione o vendedor.'); return; }
        const payload = { kind: f.kind, phrase: f.phrase.trim() };
        if (f.scope === 'store') {
            payload.subdomain = subdomain;
            payload.store_id = Number(f.store_id);
        } else {
            payload.salesman_id = Number(f.salesman_id);
        }
        try {
            setSavingPhrase(true);
            setError('');
            await apiService.awonCreateKeyPhrase(payload);
            setNewPhrase({ ...emptyForm, kind: f.kind, scope: f.scope, store_id: f.store_id, salesman_id: f.salesman_id });
            await reloadPhrases();
        } catch (err) {
            setError(err.message || 'Erro ao criar frase');
        } finally {
            setSavingPhrase(false);
        }
    };

    const saveEditPhrase = async () => {
        if (!editingPhrase || !editingPhrase.phrase.trim()) return;
        try {
            setActioning((prev) => ({ ...prev, [`p${editingPhrase.id}`]: true }));
            await apiService.awonUpdateKeyPhrase({
                id: editingPhrase.id, subdomain, phrase: editingPhrase.phrase.trim(),
            });
            setEditingPhrase(null);
            await reloadPhrases();
        } catch (err) {
            setError(err.message || 'Erro ao editar frase');
        } finally {
            setActioning((prev) => ({ ...prev, [`p${editingPhrase.id}`]: false }));
        }
    };

    const updatePhrase = async (phrase, changes) => {
        try {
            setActioning((prev) => ({ ...prev, [`p${phrase.id}`]: true }));
            await apiService.awonUpdateKeyPhrase({ id: phrase.id, subdomain, ...changes });
            const phrasesData = await apiService.awonGetKeyPhrases(subdomain);
            setPhrases(phrasesData.phrases || []);
        } catch (err) {
            setError(err.message || 'Erro ao atualizar frase');
        } finally {
            setActioning((prev) => ({ ...prev, [`p${phrase.id}`]: false }));
        }
    };

    // --- helpers de exibição ----------------------------------------------
    // A API entrega todos os horários já em hora do Brasil (created convertido
    // de UTC; window_start/end já vêm do relógio do dispositivo). Formatamos a
    // string literalmente para não sofrer conversão pelo fuso do navegador.
    const fmtDate = (value) => {
        if (!value) return '—';
        const m = String(value).match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
        if (!m) return String(value);
        return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`;
    };

    // horário de hoje só como HH:MM; de outro dia, com a data (dd/mm HH:MM) — já em hora do Brasil
    const fmtHoraOuData = (value) => {
        const m = String(value || '').match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
        if (!m) return '—';
        const hoje = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
        return `${m[1]}-${m[2]}-${m[3]}` === hoje ? `${m[4]}:${m[5]}` : `${m[3]}/${m[2]} ${m[4]}:${m[5]}`;
    };

    // data gravada em UTC pelo backend (juiz de IA, interruptor) -> horário de Brasília
    const fmtUtc = (value) => {
        if (!value) return '—';
        const iso = /[zZ]|[+-]\d{2}:?\d{2}$/.test(String(value)) ? String(value) : `${value}Z`;
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return String(value);
        return d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit',
            year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const fmtDuration = (sec) => {
        if (sec == null) return '—';
        const s = Math.round(sec);
        return `${Math.floor(s / 60)}m${String(s % 60).padStart(2, '0')}s`;
    };

    // filas do cliente selecionado (o card mostrava a soma do worker1 de todos os tenants)
    const tenantQueue = (queue?.tenants || []).find((t) => t.subdomain === subdomain) || null;
    const transcribeQ = tenantQueue?.transcribe || null;           // estágio central: onde os chunks esperam
    // motor que transcreve hoje: o transcriber da Aliado só aparece quando a empresa usa ele
    const ENGINES = { elevenlabs: 'ElevenLabs', whisper: 'Whisper', nemotron: 'Nemotron', gemini: 'Gemini', gemini_diar: 'Gemini' };
    const engine = tenantQueue?.transcription_engine || 'elevenlabs';
    const engineLabel = engine === 'elevenlabs'
        ? 'ElevenLabs'
        : `transcriber da Aliado (${ENGINES[engine] || engine})`;
    const dlqTotal = ['transcribe_dlq', 'pipeline_dlq', 'behavior_dlq']
        .reduce((n, k) => n + (tenantQueue?.[k]?.visible || 0), 0);
    // horas em HH:MM (7,85 h -> 07:51)
    const fmtHours = (h) => {
        if (h == null) return '—';
        const min = Math.round(h * 60);
        return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
    };
    const soundTitle = (s) => ['Horas com som por dia (% do total gravado):', '',
        ...(s.recorded_hours_by_day || []).map((d) => `• ${d.day.split('-').reverse().join('/')}: ${fmtHours(d.hours_sound)} (${pct(d.hours_sound || 0, d.hours)})`)]
        .join('\n');
    const hoursTitle = (s) => ['Horas gravadas por dia:', '',
        ...(s.recorded_hours_by_day || []).map((d) => `• ${d.day.split('-').reverse().join('/')}: ${fmtHours(d.hours)}`)]
        .join('\n');
    const fmtMinutes = (sec) => (sec == null ? '—' : sec < 60 ? `${Math.round(sec)}s` : `${Math.round(sec / 60)} min`);
    const pct = (a, b) => (b ? `${Math.round((100 * a) / b)}%` : '—');
    const REASONS = {
        valida: 'válida (com cliente)', sem_fala: 'sem fala', sem_vendedor: 'sem a voz do vendedor',
        so_funcionarios: 'só funcionários', sem_cliente: 'sem cliente', sem_dialogo: 'sem diálogo',
        janela_curta: 'gravação curta', erro_transcricao: 'erro de transcrição',
    };

    const statusBadge = (s) => (
        <span className={`awon-badge awon-status-${s.status || 'open'}`}>{STATUS_LABEL[s.status] || s.status || 'open'}</span>
    );

    // motivos de needs_review gravados pelo worker (SessionManager)
    const REVIEW_REASONS = {
        overlap: 'vozes sobrepostas no fim — pareamento incerto',
        no_client_voice: 'sem voz de cliente capturada — pareou por ordem (LIFO)',
        low_client_match: 'voz do cliente não casou com nenhum atendimento aberto',
        unclosed: 'início sem fim detectado',
        forced_end: 'fim forçado (fluxo encerrado)',
        split: 'parte de um recorte dividido',
        seller_inferred: 'vendedora inferida pelo juiz',
        interleaved: 'intercalado com outro atendimento',
        not_real_sale: 'não parece atendimento de venda real',
        no_clear_seller_voice: 'voz da vendedora não reconhecida',
        nested: 'atendimento aninhado em outro',
        end_recovered_finalize: 'fim recuperado no encerramento do fluxo',
        many_clients: 'muitas vozes de cliente (pode ser mais de um atendimento)',
        unclear_boundaries: 'início ou fim do atendimento incerto',
    };
    // vários motivos juntos ("split+seller_inferred"): cada um traduzido
    const reasonLabel = (r) => String(r || '').split('+').map((x) => REVIEW_REASONS[x] || x).join(' · ');

    const discoveredPending = phrases.filter((p) => p.source === 'discovered' && !p.active);
    const activePhrases = phrases.filter((p) => p.active);

    // duas listas: atendimentos para REVISÃO (needs_review) e RECORTADOS (já ok).
    // descartados não entram em nenhuma das duas.
    // listas do juiz de IA: aprovados com confiança >= 90% / reprovados com >= 75% e ainda sem ação humana (Aceitar decisão da IA/Limpar)
    const isAiOk = (s) => s.ai_decision === 'aprovado_ia' && !s.ai_human_action;
    const isAiNo = (s) => s.ai_decision === 'reprovado_ia' && !s.ai_human_action;
    const esperandoList = aguardando ? sessions.filter((s) => aguardando.ids.has(s.id)) : [];

    // agrupamento por DATA (do window_start), com expand/collapse ao clicar na data
    const dayKey = (v) => {
        const m = String(v || '').match(/(\d{4})-(\d{2})-(\d{2})/);
        return m ? `${m[1]}-${m[2]}-${m[3]}` : 'sem-data';
    };
    const dayLabel = (k) => {
        const m = k.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        return m ? `${m[3]}/${m[2]}/${m[1]}` : k;
    };
    const groupByDay = (list) => {
        const g = {};
        list.forEach((s) => { const k = dayKey(s.window_start); (g[k] = g[k] || []).push(s); });
        return Object.keys(g).sort().reverse().map((k) => [k, g[k]]);  // data mais recente primeiro
    };
    const toggleDay = (key) => setOpenDays((prev) => {
        const n = new Set(prev);
        if (n.has(key)) n.delete(key); else n.add(key);
        return n;
    });
    const renderGrouped = (list, prefix) => groupByDay(list).map(([day, items]) => {
        const key = `${prefix}:${day}`;
        const open = openDays.has(key);
        return (
            <div key={key} className="awon-day-group">
                <button type="button" className={`awon-day-header awon-day-${prefix}`} onClick={() => toggleDay(key)}>
                    <span className="awon-day-caret">{open ? '▾' : '▸'}</span>
                    <span className="awon-day-label">{dayLabel(day)}</span>
                    <span className="awon-count">{items.length}</span>
                </button>
                {open && <div className="awon-sessions">{items.map((s) => renderSession(s, prefix))}</div>}
            </div>
        );
    });

    // nível de cor da confiança (0-100): alto >=75, médio >=45, baixo < 45
    const confLevel = (v) => (v >= 75 ? 'high' : v >= 45 ? 'mid' : 'low');
    const confChips = (s) => (
        (s.confidence_start != null || s.confidence_end != null) && (
            <span className="awon-conf" title="Confiança do recorte (início / fim), 0–100">
                {s.confidence_start != null && (
                    <span className={`awon-conf-chip awon-conf-${confLevel(s.confidence_start)}`}>
                        início {s.confidence_start}%
                    </span>
                )}
                {s.confidence_end != null && (
                    <span className={`awon-conf-chip awon-conf-${confLevel(s.confidence_end)}`}>
                        fim {s.confidence_end}%
                    </span>
                )}
            </span>
        )
    );

    // recorte dividido (atendimentos emendados): a origem lista os filhos; o filho aponta a origem
    const splitInfo = (s) => s.diarization?.split || null;
    const splitKids = (s) => (s.status === 'split' && Array.isArray(s.diarization?.split_children)
        ? s.diarization.split_children : []);
    const fmtHora = (v) => { const m = String(v || '').match(/[T ](\d{2}:\d{2})/); return m ? m[1] : '—'; };
    const splitBadges = (s) => {
        const sp = splitInfo(s);
        const kids = splitKids(s);
        return (<>
            {kids.length > 0 && (
                <span className="awon-badge awon-badge-split" title="Tinha atendimentos de clientes diferentes emendados: virou um recorte por atendimento">
                    ✂ dividido em {kids.length}
                </span>
            )}
            {sp && (
                <button type="button" className="awon-badge awon-badge-split awon-badge-link"
                        title={`Abrir o recorte completo #${sp.parent}, de onde este atendimento saiu`}
                        onClick={(e) => { e.stopPropagation(); irParaRecorte(sp.parent); }}>
                    ✂ parte {sp.index} de {sp.count} · #{sp.parent}
                </button>
            )}
            {sp?.vendedor_inferido && (
                <span className="awon-badge awon-badge-warn" title="A voz da vendedora não foi reconhecida; o juiz indicou qual voz atende. Nunca aprova sozinho.">
                    vendedora inferida
                </span>
            )}
            {sp?.intercalado && (
                <span className="awon-badge awon-badge-warn" title="Acontece ao mesmo tempo que outro atendimento (a vendedora alterna entre clientes). Nunca aprova sozinho.">
                    intercalado
                </span>
            )}
        </>);
    };
    // enviado ao n8n: fechado e com o áudio já na pasta da loja (fora de awon/, é o que dispara o n8n)
    const enviadoN8n = (s) => s.status === 'closed' && !String(s.audio_filename || '').startsWith('awon/');
    // onde está um recorte (o destino de cada parte de um recorte dividido)
    const listaDoRecorte = (k) => {
        if (isAiOk(k)) return { nome: 'Aprovado pela IA', cls: 'aiok' };
        if (isAiNo(k)) return { nome: 'Reprovado pela IA', cls: 'aino' };
        if (k.needs_review && k.status !== 'discarded') return { nome: 'Em revisão', cls: 'review' };
        if (k.status === 'discarded') return { nome: 'Descartado', cls: 'discarded' };
        if (enviadoN8n(k)) return { nome: 'Enviado ao n8n', cls: 'cut' };
        return { nome: k.status, cls: 'other' };
    };
    const splitBox = (s) => {
        const sp = splitInfo(s);
        const kids = splitKids(s);
        if (kids.length) {
            return (
                <div className="awon-split-box">
                    <strong>✂ Recorte dividido em {kids.length} atendimentos</strong>
                    <span className="awon-muted"> — o áudio abaixo é o recorte completo, guardado só para consulta: não espera
                        nenhuma ação. Cada atendimento virou um recorte separado:</span>
                    <ul>
                        {kids.map((id, i) => {
                            const k = sessions.find((x) => x.id === id);
                            const onde = k ? listaDoRecorte(k) : null;
                            return (
                                <li key={id}>
                                    <button type="button" className="awon-link" onClick={() => irParaRecorte(id)}>
                                        Parte {i + 1} · #{id}
                                    </button>
                                    {' → '}
                                    {onde ? <span className={`awon-lista-chip awon-lista-${onde.cls}`}>{onde.nome}</span>
                                        : <span className="awon-muted">carregando…</span>}
                                    {k && <> · {fmtHora(k.window_start)}–{fmtHora(k.window_end)} · {fmtDuration(k.duration)}
                                        {k.ai_decision && <> · 🤖 {AI_LABEL[k.ai_decision] || k.ai_decision}
                                            {k.ai_review?.confidence != null ? ` ${k.ai_review.confidence}%` : ''}</>}</>}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            );
        }
        if (sp) {
            return (
                <div className="awon-split-box">
                    <strong>✂ Parte {sp.index} de {sp.count}</strong> do recorte{' '}
                    <button type="button" className="awon-link" onClick={() => irParaRecorte(sp.parent)}>
                        #{sp.parent} (ver o atendimento completo)
                    </button>
                    {sp.juiz?.motivo && <div className="awon-ai-reason">Na divisão: {sp.juiz.motivo}</div>}
                </div>
            );
        }
        return null;
    };

    const renderSession = (s, mode = '') => (
        <div key={s.id} id={`awon-session-${s.id}`}
             className={`awon-session ${s.needs_review ? 'awon-session-review' : 'awon-session-ok'} ${mode === 'aiok' ? 'awon-session-aiok' : mode === 'aino' ? 'awon-session-aino' : ''}`}>
            <div
                className="awon-session-row"
                onClick={() => setExpandedSession(expandedSession === s.id ? null : s.id)}
            >
                <div className="awon-session-main">
                    <strong>#{s.id}</strong>
                    <span>{s.salesman_name}<FaseBadge fase={fasesVend[String(s.salesman_id)]} compacto /></span>
                    {s.store_name && <span className="awon-muted">🏬 {s.store_name}</span>}
                    {statusBadge(s)}
                    <span className={`awon-badge awon-badge-cut awon-cut-${s.cut_method || 'phrase'}`}
                          title="Método de recorte">
                        {(s.cut_method || 'phrase') === 'llm' ? 'LLM' : s.cut_method === 'split' ? 'divisão' : 'frase'}
                    </span>
                    {confChips(s)}
                    {splitBadges(s)}
                    {s.ai_decision && (
                        <span className={`awon-badge awon-badge-ai awon-ai-${s.ai_decision}`}
                              title={s.ai_review?.reason || ''}>
                            🤖 {AI_LABEL[s.ai_decision] || s.ai_decision}
                            {s.ai_review?.confidence != null ? ` · ${s.ai_review.confidence}%` : ''}
                            {s.ai_applied && mode !== 'review' ? ' · aplicado' : ''}
                        </span>
                    )}
                    {s.needs_review && mode === 'review' && (
                        <span className="awon-badge awon-badge-review"
                              title={[s.review_reason ? reasonLabel(s.review_reason) : '',
                                      ...((s.diarization_result && s.diarization_result.clareza
                                          && s.diarization_result.clareza.falhas) || [])].filter(Boolean).join('\n')}>
                            revisar{s.review_reason ? `: ${reasonLabel(s.review_reason)}` : ''}
                        </span>
                    )}
                </div>
                <div className="awon-session-meta">
                    <span>{fmtDate(s.window_start)}</span>
                    <span>{fmtDuration(s.duration)}</span>
                    <span>{s.total_chunks} chunks</span>
                    {(mode === 'aiok' || mode === 'aino') && (
                        <span className="awon-row-actions" onClick={(e) => e.stopPropagation()}>
                            {!s.ai_applied ? (<>
                                {/* forçar só o CONTRÁRIO do que a IA decidiu: aprovado -> reprovar; reprovado -> aprovar */}
                                {mode === 'aino' && (
                                    <button className="awon-btn awon-btn-sm awon-btn-ok awon-btn-force" disabled={actioning[s.id]}
                                        title="Decidir por cima da IA: enviar o áudio ao pipeline"
                                        onClick={() => aiAction(s, 'forcar_aprovar')}>
                                        Forçar Aprovar
                                    </button>
                                )}
                                {mode === 'aiok' && (
                                    <button className="awon-btn awon-btn-sm awon-btn-danger awon-btn-force" disabled={actioning[s.id]}
                                        title="Decidir por cima da IA: mandar o recorte para o descarte"
                                        onClick={() => aiAction(s, 'forcar_reprovar')}>
                                        Forçar Reprovar
                                    </button>
                                )}
                                <button className={`awon-btn awon-btn-sm ${mode === 'aiok' ? 'awon-btn-ok' : 'awon-btn-danger'}`}
                                    disabled={actioning[s.id]}
                                    title={mode === 'aiok' ? 'Seguir a IA: enviar o recorte ao n8n' : 'Seguir a IA: descartar o recorte'}
                                    onClick={() => aiAction(s, 'continuar')}>
                                    {mode === 'aiok' ? '✓ Aceitar decisão da IA' : '✕ Aceitar decisão da IA'}
                                </button>
                                <button className="awon-btn awon-btn-sm" disabled={actioning[s.id]}
                                    title="Rodar o juiz de IA de novo (com o prompt atual)"
                                    onClick={() => aiAction(s, 'voltar_analise')}>
                                    {actioning[s.id] === 'voltar_analise' ? '⏳ Analisando…' : '↺ Voltar para análise'}
                                </button>
                            </>) : (<>
                                {mode === 'aino' && (
                                    <button className="awon-btn awon-btn-sm awon-btn-ok awon-btn-force" disabled={actioning[s.id]}
                                        title="O automático descartou: trazer de volta e enviar o áudio ao pipeline"
                                        onClick={() => aiAction(s, 'forcar_aprovar')}>
                                        Forçar Aprovar
                                    </button>
                                )}
                                <button className="awon-btn awon-btn-sm" disabled={actioning[s.id]}
                                    title="Tirar da lista (a decisão da IA já foi aplicada)"
                                    onClick={() => aiAction(s, 'limpar')}>
                                    Limpar
                                </button>
                            </>)}
                        </span>
                    )}
                    {s.needs_review && mode === 'review' && (
                        <span className="awon-row-actions" onClick={(e) => e.stopPropagation()}>
                            {!s.ai_applied && (
                                <button
                                    className="awon-btn awon-btn-sm awon-btn-ai"
                                    disabled={actioning[s.id] || !!correcao}
                                    title="A IA errou? Diga o que seria o correto: a IA ajusta o prompt do juiz desta empresa e reavalia"
                                    onClick={() => abrirCorrecao(s)}
                                >
                                    ✏️ Corrigir análise
                                </button>
                            )}
                            {!s.ai_applied && (
                                <button
                                    className="awon-btn awon-btn-sm awon-btn-ai"
                                    disabled={actioning[s.id] || reavaliacao?.status === 'rodando'}
                                    title="Pedir ao juiz de IA uma nova avaliação deste recorte (com o prompt atual)"
                                    onClick={() => novaAvaliacao(s)}
                                >
                                    🔄 Nova avaliação da IA
                                </button>
                            )}
                            <button
                                className="awon-btn awon-btn-ok awon-btn-sm"
                                disabled={actioning[s.id]}
                                title="Confirmar que é um atendimento válido"
                                onClick={() => reviewSession(s, 'approve')}
                            >
                                ✓ Aprovar
                            </button>
                            <button
                                className="awon-btn awon-btn-danger awon-btn-sm"
                                disabled={actioning[s.id]}
                                title="Não é um atendimento real — descartar"
                                onClick={() => setConfirmDiscard(s)}
                            >
                                ✕ Descartar
                            </button>
                        </span>
                    )}
                    <span className="awon-expand">{expandedSession === s.id ? '▲' : '▼'}</span>
                </div>
            </div>

            {expandedSession === s.id && (
                <div className="awon-session-detail">
                    {splitBox(s)}
                    {s.ai_review && (
                        <div className={`awon-ai-box awon-ai-${s.ai_decision}`}>
                            <strong>🤖 Juiz de IA:</strong> {AI_LABEL[s.ai_decision] || s.ai_decision}
                            {s.ai_review.confidence != null && <> · confiança {s.ai_review.confidence}%</>}
                            {s.ai_review.reason && <div className="awon-ai-reason">{s.ai_review.reason}</div>}
                            <div className="awon-muted">
                                {s.ai_review.model || ''}{s.ai_review.at ? ` · ${fmtUtc(s.ai_review.at)}` : ''}
                                {s.ai_review.trigger === 'voltar_analise' ? ' · reanalisado' : ''}
                                {s.ai_human_action ? ` · ${s.ai_human_action} por ${s.ai_human_by || '—'}` : ''}
                            </div>
                        </div>
                    )}
                    {s.has_audio ? (
                        <div className="awon-audio">
                            <AudioSobDemanda subdomain={subdomain} params={{ session_id: s.id }} />
                        </div>
                    ) : (
                        <p className="awon-muted">Sem áudio disponível.</p>
                    )}

                    {s.diarization && Array.isArray(s.diarization.turns) && s.diarization.turns.length > 0 ? (
                        <div className="awon-transcription">
                            <h4>Transcrição</h4>
                            {Array.isArray(s.diarization.speakers) && s.diarization.speakers.length > 0 && (
                                <div className="awon-diar-simblock">
                                    <h5 className="awon-diar-simtitle">
                                        Similaridade de vozes
                                        <span className="awon-diar-simhint"> — comparado com a voz cadastrada do vendedor</span>
                                    </h5>
                                    <div className="awon-diar-speakers">
                                        {s.diarization.speakers.map((sp, i) => (
                                            <span key={i} className={sp.is_seller ? 'awon-diar-spk-tag seller' : 'awon-diar-spk-tag'}>
                                                {sp.name || sp.label}: {Number(sp.seller_sim).toFixed(2)}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="awon-diar-turns">
                                {s.diarization.turns.map((t, i) => (
                                    <div key={i} className="awon-diar-turn">
                                        <span className="awon-diar-ts">{`${String(Math.floor(t.t0 / 60)).padStart(2, '0')}:${String(Math.floor(t.t0 % 60)).padStart(2, '0')}`}</span>
                                        <span className="awon-diar-spk">{t.speaker_name || t.speaker}</span>
                                        <span className="awon-diar-text">{t.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : s.transcription && (
                        <div className="awon-transcription">
                            <h4>Transcrição</h4>
                            <pre>{s.transcription}</pre>
                        </div>
                    )}

                    {Array.isArray(s.events) && s.events.length > 0 && (
                        <div className="awon-events">
                            <h4>Eventos</h4>
                            <ul>
                                {s.events.map((ev, i) => (
                                    <li key={i}>
                                        <code>{typeof ev === 'string' ? ev : JSON.stringify(ev)}</code>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="awon-session-info">
                        {s.seller_speaker_similarity != null && (
                            <span>Similaridade vendedor: {Number(s.seller_speaker_similarity).toFixed(2)}</span>
                        )}
                        {s.audio_filename && <span className="awon-muted">{s.audio_filename}</span>}
                    </div>

                    <div className="awon-actions">
                        {s.needs_review && (
                            <button
                                className="awon-btn awon-btn-ok"
                                disabled={actioning[s.id]}
                                onClick={() => reviewSession(s, 'approve')}
                            >
                                ✓ Aprovar
                            </button>
                        )}
                        {s.status !== 'discarded' && (
                            <button
                                className="awon-btn awon-btn-danger"
                                disabled={actioning[s.id]}
                                onClick={() => setConfirmDiscard(s)}
                            >
                                ✕ Descartar
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    if (loading && !stats) {
        return <Loading message="Carregando monitor AWON..." fullScreen />;
    }

    return (
        <div className="awon-monitor">
            {reavaliacao && (
                <div className="awon-modal-overlay" onClick={() => reavaliacao.status !== 'rodando' && setReavaliacao(null)}>
                    <div className="awon-modal awon-modal-reav" role="dialog" aria-modal="true" aria-labelledby="awon-reav-titulo"
                         onClick={(e) => e.stopPropagation()}>
                        <h3 className="awon-modal-title" id="awon-reav-titulo">
                            🤖 Nova avaliação da IA · recorte #{reavaliacao.session.id}
                        </h3>
                        <p className="awon-reav-sub">
                            {reavaliacao.session.salesman_name || `vendedor #${reavaliacao.session.salesman_id}`}
                            {' · '}{fmtDate(reavaliacao.session.window_start)}{' · '}{fmtDuration(reavaliacao.session.duration)}
                        </p>
                        {reavaliacao.status === 'rodando' && (
                            <div className="awon-reav-loading">
                                <span className="awon-spinner" aria-hidden="true" />
                                <span>A IA está relendo o recorte… pode levar até 1 minuto.</span>
                            </div>
                        )}
                        {reavaliacao.status === 'erro' && (
                            <p className="awon-modal-body awon-reav-erro">Não foi possível reavaliar: {reavaliacao.erro}</p>
                        )}
                        {reavaliacao.status === 'pronto' && (() => {
                            const r = reavaliacao.res || {};
                            const antes = r.before || {};
                            const card = (titulo, d) => {
                                const dec = d.decision;
                                const info = REAV_LABEL[dec] || { txt: dec ? (AI_LABEL[dec] || dec) : 'Sem avaliação', cls: 'none', icon: '·' };
                                return (
                                    <div className={`awon-reav-card awon-reav-${info.cls}`}>
                                        <span className="awon-reav-card-title">{titulo}</span>
                                        <span className="awon-reav-dec">{info.icon} {dec === 'dividido' && r.children ? `Dividido em ${r.children.length} partes` : info.txt}</span>
                                        {d.confidence != null && <span className="awon-reav-conf">confiança {d.confidence}%</span>}
                                        {d.reason && <p className="awon-reav-reason">{d.reason}</p>}
                                    </div>
                                );
                            };
                            const prox = r.decision === 'dividido'
                                ? `O recorte foi dividido em ${(r.children || []).map((c) => `#${c}`).join(', ')}; cada parte passa pelo juiz e aparece na lista.`
                                : r.decision === 'indeciso' ? 'Continua na lista Em revisão: uma pessoa decide.'
                                : r.applied ? (r.decision === 'aprovado_ia' ? 'Aplicado pelo automático: foi para o n8n e vira atendimento.' : 'Aplicado pelo automático: o recorte foi descartado.')
                                : `Foi para a lista ${r.decision === 'aprovado_ia' ? '"Aprovados pela IA"' : '"Reprovados pela IA"'} e só vale depois de Aceitar decisão da IA (o automático está desligado).`;
                            return (
                                <>
                                    <div className="awon-reav-grid">
                                        {card('Antes', antes)}
                                        <span className="awon-reav-arrow" aria-hidden="true">→</span>
                                        {card('Agora', r)}
                                    </div>
                                    <p className="awon-modal-note">{prox}</p>
                                </>
                            );
                        })()}
                        <div className="awon-modal-actions">
                            {reavaliacao.status === 'pronto' && (
                                <button className="awon-btn awon-btn-ghost" onClick={() => {
                                    const r = reavaliacao.res || {};
                                    setReavaliacao(null);
                                    irParaRecorte(r.decision === 'dividido' && r.children?.length ? r.children[0] : reavaliacao.session.id);
                                }}>Ver o recorte</button>
                            )}
                            <button className="awon-btn awon-btn-ok" disabled={reavaliacao.status === 'rodando'}
                                    onClick={() => setReavaliacao(null)}>
                                {reavaliacao.status === 'rodando' ? 'Aguarde…' : 'Fechar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {correcao && (() => {
                const c = correcao;
                const s = c.session;
                const ocupado = c.fase === 'analisando' || c.fase === 'aplicando';
                const cartao = (titulo, d, extra) => {
                    const info = REAV_LABEL[d?.decision] || { txt: d?.decision ? (AI_LABEL[d.decision] || d.decision) : 'Sem avaliação', cls: 'none', icon: '·' };
                    return (
                        <div className={`awon-reav-card awon-reav-${info.cls}`}>
                            <span className="awon-reav-card-title">{titulo}</span>
                            <span className="awon-reav-dec">{info.icon} {info.txt}</span>
                            {d?.confidence != null && <span className="awon-reav-conf">confiança {d.confidence}%</span>}
                            {d?.reason && <p className="awon-reav-reason">{d.reason}</p>}
                            {extra}
                        </div>
                    );
                };
                const antes = c.proposta?.ai_before || { decision: s.ai_decision, confidence: s.ai_review?.confidence, reason: s.ai_review?.reason };
                const fin = c.final?.final || {};
                const prox = fin.decision === 'indeciso' ? 'Continua na lista Em revisão.'
                    : fin.decision === 'dividido' ? `Foi dividido em ${(fin.children || []).map((x) => `#${x}`).join(', ')}; cada parte passa pelo juiz.`
                    : fin.applied ? 'Aplicado pelo automático.'
                    : fin.decision ? `Foi para a lista ${fin.decision === 'aprovado_ia' ? '"Aprovados pela IA"' : '"Reprovados pela IA"'}.` : '';
                return (
                    <div className="awon-modal-overlay" onClick={() => !ocupado && fecharCorrecao()}>
                        <div className="awon-modal awon-modal-corr" role="dialog" aria-modal="true" aria-labelledby="awon-corr-titulo"
                             onClick={(e) => e.stopPropagation()}>
                            <h3 className="awon-modal-title" id="awon-corr-titulo">✏️ Corrigir análise · recorte #{s.id}</h3>
                            <p className="awon-reav-sub">
                                {s.salesman_name || `vendedor #${s.salesman_id}`}{' · '}{fmtDate(s.window_start)}{' · '}{fmtDuration(s.duration)}
                            </p>
                            {(c.fase === 'editar' || c.fase === 'erro') && (
                                <>
                                    {cartao('O que a IA avaliou', antes)}
                                    {c.pendente && (
                                        <p className="awon-corr-aviso">Você não aceitou a proposta anterior. Ajuste o texto abaixo — a IA vai
                                            levar em conta a tentativa anterior e o seu comentário.</p>
                                    )}
                                    {c.fase === 'erro' && <p className="awon-reav-erro">{c.erro}</p>}
                                    <label className="awon-corr-label" htmlFor="awon-correcao-texto">O que seria a avaliação correta deste recorte?</label>
                                    <textarea id="awon-correcao-texto" className="awon-corr-texto" rows={5} value={c.texto}
                                              placeholder="Ex.: É atendimento de venda: a vendedora recomenda produtos e a cliente escolhe um deles. Recomendação com resposta da cliente deve ser aprovada."
                                              onChange={(e) => setCorrecao({ ...c, texto: e.target.value })} />
                                    <p className="awon-modal-note">A IA propõe um ajuste geral no prompt do juiz desta empresa (vale para os
                                        próximos recortes, nunca para outra empresa) e mostra como este recorte ficaria. Nada muda até você aceitar.</p>
                                </>
                            )}
                            {ocupado && (
                                <div className="awon-reav-loading">
                                    <span className="awon-spinner" aria-hidden="true" />
                                    <span>{c.fase === 'analisando'
                                        ? 'A IA está ajustando o prompt e reavaliando o recorte… pode levar até 1 minuto.'
                                        : 'Gravando a nova versão do prompt e reavaliando o recorte…'}</span>
                                </div>
                            )}
                            {c.fase === 'proposta' && (() => {
                                const p = c.proposta;
                                return (
                                    <>
                                        {p.understanding && (<div className="awon-corr-bloco"><span className="awon-corr-rotulo">O que a IA entendeu</span><p>{p.understanding}</p></div>)}
                                        <div className="awon-corr-bloco"><span className="awon-corr-rotulo">O que muda no prompt</span><p>{p.change_summary}</p></div>
                                        <div className="awon-corr-bloco">
                                            <span className="awon-corr-rotulo">
                                                Prompt ajustado
                                                <button type="button" className="awon-link" onClick={() => setCorrecao({ ...c, verPrompt: !c.verPrompt })}>
                                                    {c.verPrompt ? 'ver só as mudanças' : 'ver o prompt inteiro'}
                                                </button>
                                            </span>
                                            {c.verPrompt ? (
                                                <pre className="awon-diff">{p.prompt_after}</pre>
                                            ) : (
                                                <div className="awon-diff" role="list">
                                                    {(p.diff || []).map((l, i) => (
                                                        <div key={i} role="listitem" className={`awon-diff-${l.tipo}`}>
                                                            <span aria-hidden="true">{l.tipo === 'adicionado' ? '+' : l.tipo === 'removido' ? '−' : ' '}</span>
                                                            {l.texto || ' '}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <span className="awon-corr-rotulo">Nova classificação</span>
                                        <div className="awon-reav-grid">
                                            {cartao('Hoje', p.ai_before)}
                                            <span className="awon-reav-arrow" aria-hidden="true">→</span>
                                            {cartao('Com o ajuste', p.eval_after)}
                                        </div>
                                        <p className="awon-modal-note">Aceitando, o ajuste vira uma nova versão do prompt do juiz (com o seu nome) e
                                            este recorte é reavaliado com ela. Não aceitando, você volta ao texto para reescrever.</p>
                                    </>
                                );
                            })()}
                            {c.fase === 'pronto' && (
                                <>
                                    <p className="awon-corr-ok">✓ Prompt do juiz atualizado (nova versão #{c.final?.prompt_id_after}). Vale para os
                                        próximos recortes desta empresa.</p>
                                    {cartao('Recorte reavaliado', fin)}
                                    {prox && <p className="awon-modal-note">{prox}</p>}
                                </>
                            )}
                            <div className="awon-modal-actions">
                                {(c.fase === 'editar' || c.fase === 'erro') && (<>
                                    <button className="awon-btn awon-btn-ghost" onClick={fecharCorrecao}>Cancelar</button>
                                    <button className="awon-btn awon-btn-ok" disabled={(c.texto || '').trim().length < 10}
                                            onClick={correcaoAnalisar}>Analisar correção</button>
                                </>)}
                                {c.fase === 'proposta' && (<>
                                    <button className="awon-btn awon-btn-ghost" onClick={correcaoNaoAceitar}>Não aceitar</button>
                                    <button className="awon-btn awon-btn-ok" onClick={correcaoAceitar}>Aceitar ajuste</button>
                                </>)}
                                {ocupado && <button className="awon-btn awon-btn-ok" disabled>Aguarde…</button>}
                                {c.fase === 'pronto' && (<>
                                    {(fin.decision === 'aprovado_ia' || fin.decision === 'reprovado_ia') && !fin.applied && (
                                        <button className={`awon-btn ${fin.decision === 'aprovado_ia' ? 'awon-btn-ok' : 'awon-btn-danger'}`}
                                                onClick={() => { setCorrecao(null); aiAction({ ...s, ai_decision: fin.decision }, 'continuar'); }}>
                                            {fin.decision === 'aprovado_ia' ? '✓' : '✕'} Aceitar decisão da IA agora
                                        </button>
                                    )}
                                    <button className="awon-btn awon-btn-ghost" onClick={() => { setCorrecao(null); irParaRecorte(s.id); }}>Ver o recorte</button>
                                    <button className="awon-btn awon-btn-ok" onClick={() => setCorrecao(null)}>Fechar</button>
                                </>)}
                            </div>
                        </div>
                    </div>
                );
            })()}
            {confirmDiscard && (
                <div className="awon-modal-overlay" onClick={() => setConfirmDiscard(null)}>
                    <div className="awon-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="awon-modal-title">Descartar atendimento</h3>
                        <p className="awon-modal-body">
                            Descartar o atendimento <strong>#{confirmDiscard.id}</strong>
                            {confirmDiscard.salesman_name ? <> de <strong>{confirmDiscard.salesman_name}</strong></> : null}?
                            <br />O áudio sai da revisão e <strong>não vai</strong> para o pipeline de feedback.
                        </p>
                        <div className="awon-modal-actions">
                            <button className="awon-btn awon-btn-ghost" onClick={() => setConfirmDiscard(null)}>
                                Cancelar
                            </button>
                            <button
                                className="awon-btn awon-btn-danger"
                                disabled={actioning[confirmDiscard.id]}
                                onClick={() => { reviewSession(confirmDiscard, 'discard'); setConfirmDiscard(null); }}
                            >
                                ✕ Descartar
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="awon-header">
                <div>
                    <h1>🎙️ AWON — Monitor do Pipeline</h1>
                    <p className="awon-subtitle">Atendimentos always-on: vendedor reconhecido pela voz, início e fim analisados pelo Jev</p>
                </div>
                <div className="awon-controls">
                    <select value={subdomain} onChange={(e) => setSubdomain(e.target.value)}>
                        {(tenants.length ? tenants : [{ subdomain, name: subdomain }]).map((t) => (
                            <option key={t.subdomain} value={t.subdomain}>{t.name || t.subdomain}</option>
                        ))}
                    </select>
                    <select value={dataIni || dataFim ? 'datas' : days} title="Período de tudo que tem data na página (menos as vozes)"
                            onChange={(e) => {
                                if (e.target.value === 'datas') {
                                    const hoje = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
                                    setDataIni(hoje); setDataFim(hoje);
                                } else { setDataIni(''); setDataFim(''); setDays(Number(e.target.value)); }
                            }}>
                        <option value={1}>Hoje</option>
                        <option value={7}>7 dias</option>
                        <option value={30}>30 dias</option>
                        <option value={90}>90 dias</option>
                        <option value="datas">Escolher datas…</option>
                    </select>
                    <label className="awon-date" title="Data início (00:00:00, horário de Brasília)">
                        De <input type="date" id="awon-data-ini" value={dataIni} max={dataFim || undefined}
                                  onChange={(e) => setDataIni(e.target.value)} />
                    </label>
                    <label className="awon-date" title="Data fim (23:59:59, horário de Brasília)">
                        até <input type="date" id="awon-data-fim" value={dataFim} min={dataIni || undefined}
                                   onChange={(e) => setDataFim(e.target.value)} />
                    </label>
                    {(dataIni || dataFim) && (
                        <button type="button" className="awon-link" onClick={() => { setDataIni(''); setDataFim(''); }}
                                title="Voltar ao atalho de dias">limpar datas</button>
                    )}
                    <button className="awon-refresh" onClick={loadAll} disabled={loading}>
                        {loading ? '⏳' : '🔄'} Atualizar
                    </button>
                    <label className="awon-auto-refresh" title="Atualiza cards, filas, comportamentos e recortes a cada 3 segundos, sem recarregar a página. Pausa quando a aba não está visível.">
                        <input type="checkbox" id="awon-auto-refresh" checked={autoRefresh}
                               onChange={(e) => setAutoRefresh(e.target.checked)} />
                        Atualizar sozinho (3 s)
                    </label>
                </div>
            </div>

            {lastUpdated && (
                <p className="awon-updated">Atualizado às {lastUpdated.toLocaleTimeString('pt-BR')}</p>
            )}
            {error && <div className="awon-error">{error}</div>}

            {/* Visão por cliente — clique seleciona o cliente detalhado abaixo */}
            {tenants.length > 1 && (
                <section className="awon-section">
                    <h2>Clientes</h2>
                    <table className="awon-table">
                        <thead>
                            <tr>
                                <th>Cliente</th>
                                <th>Atendimentos</th>
                                <th title={TIP.colRevisao}>P/ revisão</th>
                                <th title={TIP.colHorasTotal}>Horas gravadas</th>
                                <th title={TIP.ultimo}>Último chunk</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* só quem usa o AWON (áudio nos últimos 30 dias); API antiga sem o campo: todos */}
                            {tenants.filter((t) => t.awon_ativo !== false).map((t) => {
                                const o = overview[t.subdomain];
                                return (
                                    <tr key={t.subdomain}
                                        className={`awon-row-click ${t.subdomain === subdomain ? 'awon-row-active' : ''}`}
                                        onClick={() => setSubdomain(t.subdomain)}
                                        title={`Ver detalhes de ${t.name || t.subdomain}`}>
                                        <td><strong>{t.name || t.subdomain}</strong> <span className="awon-muted">{t.subdomain}</span></td>
                                        <td>{o ? o.sessions.total : '…'}</td>
                                        {(() => {
                                            const rev = o ? (o.sessions.para_revisao ?? o.sessions.needs_review) : null;
                                            return <td>{rev == null ? '…' : rev > 0 ? <strong>{rev}</strong> : 0}</td>;
                                        })()}
                                        <td>{o ? fmtHours((o.by_salesman || []).reduce((n, v) => n + (v.recorded_hours || 0), 0)) : '…'}</td>
                                        <td>{o ? fmtDate(o.chunks.last_chunk_at) : '…'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </section>
            )}

            {/* Cards de visão geral do cliente selecionado */}
            {stats && stats.company_name && (
                <h2 className="awon-client-title">{stats.company_name}</h2>
            )}
            {stats && (
            <section className={`awon-group awon-group-common ${fechadas.has('audio') ? 'awon-group-collapsed' : ''}`}>
                <div className="awon-group-head awon-group-toggle" {...cabecalhoSecao('audio')}>
                    {caretSecao('audio')}
                    <span className="awon-group-tag">Comum</span>
                    <h2>Áudio recebido</h2>
                    <p>Trechos de áudio que chegam do app e a transcrição. Servem aos dois workers.</p>
                </div>
                <div className="awon-cards">
                    <div className="awon-card" title={TIP.chunks}>
                        <span className="awon-card-value">{stats.chunks.received - stats.chunks.silence}</span>
                        <span className="awon-card-label">Chunks com som ({periodoCurto})</span>
                        <span className="awon-card-detail">
                            {stats.chunks.pending} aguardando
                            {stats.chunks.discarded != null && <> · <span title={TIP.descartados}>{stats.chunks.discarded} descartados</span></>}
                            {stats.chunks.in_session != null && <> · {stats.chunks.in_session} válidos para atendimento</>}
                        </span>
                        <span className="awon-card-detail awon-muted">
                            🔇 {stats.chunks.silence} silenciosos (ignorados)
                        </span>
                    </div>
                    <div className={`awon-card ${(tenantQueue?.transcribe_dlq?.visible || 0) > 0 ? 'awon-card-warn' : ''}`} title={TIP.fila}>
                        {transcribeQ ? (
                            <>
                                <span className="awon-card-value">{transcribeQ.visible ?? '?'}</span>
                                <span className="awon-card-label">Fila de transcrição</span>
                                <span className="awon-card-detail">
                                    {engineLabel} · {transcribeQ.in_flight ?? 0} em processamento · DLQ {tenantQueue?.transcribe_dlq?.visible ?? 0}
                                </span>
                            </>
                        ) : (
                            <>
                                <span className="awon-card-value awon-card-value-small">{engineLabel}</span>
                                <span className="awon-card-label">Transcrição</span>
                                <span className="awon-card-detail">
                                    {queue?.enabled ? 'sem fila única: cada worker transcreve o próprio áudio' : 'pipeline desligado'}
                                </span>
                            </>
                        )}
                    </div>
                    <div className="awon-card" title={TIP.ultimo}>
                        <span className="awon-card-value awon-card-value-small">
                            {stats.chunks.last_chunk_at ? fmtDate(stats.chunks.last_chunk_at) : '—'}
                        </span>
                        <span className="awon-card-label">Último chunk</span>
                        {(stats.chunks.last_by_salesman || []).length > 0 && (
                            <div className="awon-last-chunk-list">
                                {stats.chunks.last_by_salesman.map((v) => (
                                    <span key={v.salesman_id} className="awon-card-detail awon-last-chunk">
                                        <span>{v.name}</span>
                                        <span>{fmtHoraOuData(v.last_chunk_at)}</span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                {queue?.enabled && dlqTotal > 0 && (
                <div className="awon-error">
                    ⚠️ Existem {dlqTotal} mensagens nas DLQs deste cliente
                    ({['transcribe_dlq', 'pipeline_dlq', 'behavior_dlq']
                        .filter((k) => tenantQueue?.[k]?.visible)
                        .map((k) => `${{ transcribe_dlq: 'transcrição', pipeline_dlq: 'recortes', behavior_dlq: 'comportamentos' }[k]} ${tenantQueue[k].visible}`)
                        .join(' · ')}) — mensagens que falharam após 5 tentativas.
                </div>
            )}

            </section>
            )}

            {/* Julgador smart (julgador isento do plano): decide os atendimentos a partir dos recortes do worker1 — as
                decisões do período (o que vai ao n8n), as rodadas, o "julgado até", o desfazer e o freio. Só carrega aberto. */}
            <section className={`awon-group awon-group-judge ${fechadas.has('julgador') ? 'awon-group-collapsed' : ''}`}>
                <div className="awon-group-head awon-group-toggle" {...cabecalhoSecao('julgador')}>
                    {caretSecao('julgador')}
                    <h2>Julgador smart</h2>
                    <p>A cada 2 horas lê o trecho de cada vendedora e decide os atendimentos a partir dos recortes do worker1: o que vai ao n8n, o que é descartado e o que fica para uma pessoa.</p>
                </div>
                {!fechadas.has('julgador') && (
                    <AwonJudgePanel subdomain={subdomain} periodo={periodo} salesmen={catalog.salesmen}
                                    indecisoLink={subdomain === deepLink.current.subdomain ? deepLink.current.indeciso : null} />
                )}
            </section>

            {/* Worker1 — recortes de atendimento */}
            <section className={`awon-group awon-group-w1 ${fechadas.has('recortes') ? 'awon-group-collapsed' : ''}`}>
                <div className="awon-group-head awon-group-toggle" {...cabecalhoSecao('recortes')}>
                    {caretSecao('recortes')}
                    <h2>Worker1 – recortes</h2>
                    <p>Corta a gravação em atendimentos (início e fim) e grava cada recorte. No modo awon-behaviour só recorta: os pontos vêm do worker2.</p>
                </div>
                {stats && (
                <div className="awon-cards">
                    <div className="awon-card" title={TIP.atendimentos}>
                        <span className="awon-card-value">{stats.sessions.total}</span>
                        <span className="awon-card-label">Atendimentos</span>
                        <span className="awon-card-detail">
                            {stats.sessions.closed} fechados · {stats.sessions.open} abertos
                        </span>
                    </div>
                    <div className={`awon-card ${(tenantQueue?.pipeline_dlq?.visible || 0) > 0 ? 'awon-card-warn' : ''}`} title={TIP.filaW1}>
                        <span className="awon-card-value">{queue?.enabled ? (tenantQueue?.pipeline?.visible ?? '—') : 'off'}</span>
                        <span className="awon-card-label">Fila do worker1</span>
                        <span className="awon-card-detail">
                            {tenantQueue?.pipeline?.in_flight ?? 0} em processamento · DLQ {tenantQueue?.pipeline_dlq?.visible ?? 0}
                        </span>
                    </div>
                </div>
                )}
            {/* Por vendedor */}
            {stats && stats.by_salesman.length > 0 && (
                <section className="awon-section">
                    <h2>Por vendedor</h2>
                    <table className="awon-table">
                        <thead>
                            <tr>
                                <th>Vendedor</th>
                                <th>Loja</th>
                                <th>Atendimentos</th>
                                <th>Fechados</th>
                                <th>P/ revisão</th>
                                <th title={TIP.horas}>Horas gravadas</th>
                                <th title={TIP.horasSom}>Horas com som</th>
                                <th>Último atendimento</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.by_salesman.map((s) => (
                                <tr key={s.salesman_id}>
                                    <td>{s.name} <span className="awon-muted">#{s.salesman_id}</span></td>
                                    <td>{s.store_name || '—'}</td>
                                    <td>{s.sessions}</td>
                                    <td>{s.closed}</td>
                                    <td>{s.needs_review > 0 ? <strong>{s.needs_review}</strong> : 0}</td>
                                    <td title={hoursTitle(s)}>{fmtHours(s.recorded_hours)}</td>
                                    <td title={soundTitle(s)}>
                                        {fmtHours(s.recorded_hours_sound)}
                                        {s.recorded_hours > 0 && <span className="awon-muted"> ({pct(s.recorded_hours_sound || 0, s.recorded_hours)})</span>}
                                    </td>
                                    <td>{fmtDate(s.last_session_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            )}

            {/* Atendimentos */}
            <section className="awon-section" ref={sessionsRef}>
                <div className="awon-section-header">
                    <h2>Atendimentos — {periodoTitulo}</h2>
                    <div className="awon-filters">
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="">Todos status</option>
                            <option value="closed">Fechados</option>
                            <option value="open">Abertos</option>
                            <option value="discarded">Descartados</option>
                        </select>
                    </div>
                </div>

                <h3 className="awon-list-title awon-list-pend">
                    Esperando o julgador <span className="awon-count">{esperandoList.length}</span>
                </h3>
                <p className="awon-muted">
                    Recortes do worker1 que o julgador smart ainda não processou
                    {aguardando?.mode === 'manual' ? ' · rodadas manuais: pelos botões em Julgador smart'
                        : aguardando?.next_round ? ` · próxima rodada às ${String(aguardando.next_round).slice(11, 16)}` : ''}.
                    As decisões dele ficam na seção Julgador smart.
                </p>
                {!aguardando ? <p className="awon-muted">Carregando…</p>
                    : esperandoList.length === 0 ? <p className="awon-muted">Nenhum recorte esperando o julgador.</p>
                        : renderGrouped(esperandoList, 'pend')}

            </section>

            {/* Início e fim dos atendimentos: o worker1 decide pelo Jev (frases-chave não são usadas — decisão 26/10) */}
            <section className="awon-section">
                <h2>Início e fim dos atendimentos</h2>
                <p className="awon-muted">
                    O início e o fim de cada atendimento são analisados pelo Jev, a partir do conteúdo da conversa
                    (abertura, despedida e troca de cliente). Frases-chave não são usadas no recorte.
                </p>
            </section>
            </section>

            {/* Comportamentos — worker2 no modo awon-behaviour (demanda, gate, pontos, custo, erros) */}
            {behavior?.enabled && (
                <section className={`awon-group awon-group-w2 ${fechadas.has('comportamentos') ? 'awon-group-collapsed' : ''}`}>
                <div className="awon-group-head awon-group-toggle" {...cabecalhoSecao('comportamentos')}>
                    {caretSecao('comportamentos')}
                    <h2>Worker2 – comportamentos</h2>
                    <p>{w2SoIndica
                        ? 'A cada 5 min avalia os últimos 20 min de gravação e indica ao julgador os comportamentos novos, com a fala que os mostra. Não dá pontos: quem pontua é o n8n, no atendimento que o julgador aprova.'
                        : 'A cada 5 min avalia os últimos 20 min de gravação e pontua os comportamentos novos. Rodada com comportamento novo vira atendimento no admin.'}</p>
                </div>

                    {behavior.steps.total === 0 ? (
                        <p className="awon-muted">Nenhuma rodada de comportamento processada no período.</p>
                    ) : (
                        <>
                            <div className="awon-cards">
                                <div className={`awon-card ${(tenantQueue?.behavior_dlq?.visible || 0) > 0 ? 'awon-card-warn' : ''}`} title={TIP.filaW2}>
                                    <span className="awon-card-value">{queue?.enabled ? (tenantQueue?.behavior?.visible ?? '—') : 'off'}</span>
                                    <span className="awon-card-label">Fila do worker2</span>
                                    <span className="awon-card-detail">
                                        {tenantQueue?.behavior?.in_flight ?? 0} em processamento · DLQ {tenantQueue?.behavior_dlq?.visible ?? 0}
                                    </span>
                                </div>
                                <div className="awon-card" title={TIP.rodadas}>
                                    <span className="awon-card-value">{behavior.steps.total}</span>
                                    <span className="awon-card-label">Janelas analisadas (até 5 min)</span>
                                    <span className="awon-card-detail">
                                        {behavior.steps.valid} com cliente ({pct(behavior.steps.valid, behavior.steps.total)})
                                        · {behavior.steps.salesmen} vendedor(es)
                                    </span>
                                </div>
                                <div className={`awon-card ${behavior.steps.errors > 0 ? 'awon-card-warn' : ''}`} title={TIP.erros}>
                                    <span className="awon-card-value">{behavior.steps.errors}</span>
                                    <span className="awon-card-label">Janelas com erro</span>
                                    <span className="awon-card-detail">{pct(behavior.steps.errors, behavior.steps.total)} do total</span>
                                </div>
                                <div className="awon-card" title={TIP.atraso}>
                                    <span className="awon-card-value awon-card-value-small">
                                        {fmtMinutes(behavior.steps.lag_median_sec)}
                                    </span>
                                    <span className="awon-card-label">Atraso mediano</span>
                                    <span className="awon-card-detail">
                                        máx. {fmtMinutes(behavior.steps.lag_max_sec)} · último {fmtDate(behavior.steps.last_processed_at)}
                                    </span>
                                </div>
                                {behavior.transcription_blocks && (
                                    <div className={`awon-card ${behavior.transcription_blocks.errors > 0 ? 'awon-card-warn' : ''}`} title={TIP.blocos}>
                                        <span className="awon-card-value">{behavior.transcription_blocks.total}</span>
                                        <span className="awon-card-label">Blocos transcritos</span>
                                        <span className="awon-card-detail">
                                            {behavior.transcription_blocks.audio_min} min de áudio ·
                                            {' '}{behavior.transcription_blocks.sem_fala} sem fala ·
                                            {' '}{behavior.transcription_blocks.errors} com erro
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="awon-grid-2">
                                <table className="awon-table">
                                    <thead>
                                        <tr><th>Dia</th><th>Janelas</th><th>Com cliente</th>{!w2SoIndica && <th>Atendimentos</th>}<th>Erros</th></tr>
                                    </thead>
                                    <tbody>
                                        {behavior.per_day.map((d) => (
                                            <tr key={d.day}>
                                                <td>{fmtDate(d.day + 'T00:00').slice(0, 10)}</td>
                                                <td>{d.steps}</td>
                                                <td>{d.valid}</td>
                                                {!w2SoIndica && <td>{d.with_record}</td>}
                                                <td>{d.errors > 0 ? <strong>{d.errors}</strong> : 0}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <table className="awon-table">
                                    <thead><tr><th>Resultado da janela</th><th>Janelas</th></tr></thead>
                                    <tbody>
                                        {behavior.reasons.map((r) => (
                                            <tr key={r.reason}>
                                                <td>{REASONS[r.reason] || r.reason}</td>
                                                <td>{r.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {behavior.behaviors.length > 0 && (
                                <div className={`awon-beh-list ${w2SoIndica ? 'awon-beh-2col' : ''}`}>
                                    {w2SoIndica
                                        ? <div className="awon-beh-head"><span>Comportamento indicado ao julgador</span><span>Indicações</span></div>
                                        : <div className="awon-beh-head"><span>Comportamento detectado</span><span>Ocorrências</span><span>Pontos</span></div>}
                                    {behavior.behaviors.map((b) => {
                                        const aberto = behOpen === b.behavior_key;
                                        return (
                                            <div key={b.behavior_key} className={`awon-beh ${aberto ? 'awon-beh-open' : ''}`}
                                                 style={{ borderLeftColor: b.color || '#c3ccd5' }}>
                                                <button type="button" className="awon-beh-row" aria-expanded={aberto}
                                                    onClick={() => setBehOpen(aberto ? null : b.behavior_key)}>
                                                    <span className="awon-beh-main">
                                                        <span className="awon-beh-caret">{aberto ? '▾' : '▸'}</span>
                                                        <span className="awon-beh-chip" style={b.color ? { background: b.color } : undefined}>
                                                            {b.icon ? `${b.icon} ` : ''}{b.display_name || b.behavior_key}
                                                        </span>
                                                        {b.question && <span className="awon-beh-question">{b.question}</span>}
                                                    </span>
                                                    <span className="awon-beh-num">{b.occurrences}</span>
                                                    {!w2SoIndica && <span className="awon-beh-num">{b.points}</span>}
                                                </button>
                                                {aberto && (
                                                    <div className="awon-beh-detail">
                                                        {(b.by_salesman || []).length === 0 ? (
                                                            <p className="awon-muted">Nenhum vendedor ativo realizou este comportamento no período.</p>
                                                        ) : (
                                                            <table className="awon-table awon-beh-sellers">
                                                                <thead><tr><th>Vendedor</th><th>Vezes</th>{!w2SoIndica && <th>Pontos</th>}</tr></thead>
                                                                <tbody>
                                                                    {b.by_salesman.map((x) => (
                                                                        <tr key={x.salesman_id}>
                                                                            <td>
                                                                                <a className="awon-beh-seller-link" target="_blank" rel="noopener noreferrer"
                                                                                    title="Abrir este vendedor na Revisão de pontos (nova aba)"
                                                                                    href={`/revisao-pontos?subdomain=${encodeURIComponent(subdomain)}`
                                                                                        + `${x.store_id ? `&store_id=${x.store_id}` : ''}&salesman_id=${x.salesman_id}`}>
                                                                                    {x.name} ↗
                                                                                </a>{' '}
                                                                                <span className="awon-muted">#{x.salesman_id}</span>
                                                                            </td>
                                                                            <td>{x.occurrences}</td>
                                                                            {!w2SoIndica && <td>{x.points}</td>}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <table className="awon-table">
                                <thead>
                                    <tr><th>Vendedor</th><th>Loja</th><th>Janelas</th><th>Com cliente</th>{!w2SoIndica && <th>Atendimentos</th>}<th>Erros</th><th>Último processamento</th></tr>
                                </thead>
                                <tbody>
                                    {behavior.by_salesman.map((s) => (
                                        <tr key={s.salesman_id}>
                                            <td>{s.name} <span className="awon-muted">#{s.salesman_id}</span></td>
                                            <td>{s.store_name || '—'}</td>
                                            <td>{s.steps}</td>
                                            <td>{s.valid}</td>
                                            {!w2SoIndica && <td>{s.with_record}</td>}
                                            <td>{s.errors > 0 ? <strong>{s.errors}</strong> : 0}</td>
                                            <td>{fmtDate(s.last_processed_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {behavior.recent_errors.length > 0 && (
                                <div className="awon-error">
                                    <strong>Erros recentes</strong>
                                    <ul>
                                        {behavior.recent_errors.map((e, i) => (
                                            <li key={i}>
                                                {fmtDate(e.at)} · vendedor #{e.salesman_id} · janela {fmtDate(e.window_start)} — {e.error}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )}
                </section>
            )}

            {/* Vozes cadastradas (embeddings de vendedor) */}
            <section className={`awon-group awon-group-common ${fechadas.has('vozes') ? 'awon-group-collapsed' : ''}`}>
                <div className="awon-section-header">
                    <div className="awon-group-head awon-group-toggle" {...cabecalhoSecao('vozes')}>
                        {caretSecao('vozes')}
                        <span className="awon-group-tag">Comum</span>
                        <h2>Vozes cadastradas</h2>
                        <p>Voz de referência de cada vendedor. Os dois workers usam para separar vendedor e cliente; sem voz cadastrada o vendedor é ignorado.</p>
                    </div>
                    <button
                        className="awon-btn awon-btn-ghost"
                        title="As URLs de áudio expiram em 5 minutos"
                        onClick={async () => {
                            try {
                                const emb = await apiService.awonGetEmbeddings(subdomain);
                                setVoices({ enrolled: emb.enrolled || [], missing: emb.missing || [] });
                            } catch (e) { setError(e.message); }
                        }}
                    >
                        🔄 Recarregar áudios
                    </button>
                </div>
                {voices.enrolled.length === 0 ? (
                    <p className="awon-muted">Nenhum vendedor com voz cadastrada.</p>
                ) : (
                    <table className="awon-table">
                        <thead>
                            <tr>
                                <th>Vendedor</th>
                                <th>Loja</th>
                                <th>Cadastrada em</th>
                                <th>Áudio de referência</th>
                            </tr>
                        </thead>
                        <tbody>
                            {voices.enrolled.map((v) => (
                                <tr key={v.id}>
                                    <td>{v.salesman_name} <span className="awon-muted">#{v.salesman_id}</span><FaseBadge fase={fasesVend[String(v.salesman_id)]} compacto /></td>
                                    <td>{v.store_name || '—'}</td>
                                    <td>{fmtDate(v.created)}</td>
                                    <td>
                                        {v.has_audio ? (
                                            <AudioSobDemanda subdomain={subdomain} params={{ embedding_id: v.id }}
                                                             className="awon-voice-audio" />
                                        ) : (
                                            <span className="awon-muted"
                                                  title="Cadastro antigo sem o caminho do áudio de origem">
                                                sem áudio registrado
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {voices.missing.length > 0 && (
                    <p className="awon-voices-missing">
                        ⚠️ Sem voz cadastrada ({voices.missing.length}) — o pipeline não processa estes vendedores:{' '}
                        {voices.missing.map((m) => `${m.salesman_name}${m.store_name ? ` (${m.store_name})` : ''}`).join(' · ')}
                    </p>
                )}
            </section>

        </div>
    );
};

export default AwonMonitor;
